'use client';
import React, { createContext, useContext, useMemo, useState } from 'react';
import { FbtProductWithPrice } from 'services/fbtItems/types';
import { CartResponse } from 'services/cart/types';

export interface FbtContextType {
  fbtProductsWithPrice: IState['fbtProductsWithPrice'];
  setFbtProductsWithPrice: React.Dispatch<
    React.SetStateAction<IState['fbtProductsWithPrice']>
  >;
  isFbtSubTotalLoading: boolean;
  setIsFbtSubTotalLoading: React.Dispatch<React.SetStateAction<boolean>>;
  subTotal: IState['subTotal'];
  setSubTotal: React.Dispatch<React.SetStateAction<IState['subTotal']>>;
  currency: IState['currency'];
  setCurrency: React.Dispatch<React.SetStateAction<IState['currency']>>;
  cartDetails: IState['cartDetails'];
  setCartDetails: React.Dispatch<React.SetStateAction<IState['cartDetails']>>;
  itemSummary: ISummary;
  setItemSummary: React.Dispatch<React.SetStateAction<ISummary>>;
}

const FbtContext = createContext<FbtContextType>(null);

interface IState {
  subTotal: {
    formattedSubtotal: string;
    unformattedSubtotal: number;
  } | null;
  fbtProductsWithPrice: FbtProductWithPrice[];
  currency: string;
  cartDetails: CartResponse;
}

export function FbtContextProvider({ children, currentPartNumber }) {
  const [isFbtSubTotalLoading, setIsFbtSubTotalLoading] = useState(false);
  const [fbtProductsWithPrice, setFbtProductsWithPrice] = useState<
    IState['fbtProductsWithPrice']
  >([]);
  const [subTotal, setSubTotal] = useState<IState['subTotal']>(null);
  const [currency, setCurrency] = useState<IState['currency']>('');
  const [cartDetails, setCartDetails] = useState<IState['cartDetails']>(null);
  const [itemSummary, setItemSummary] = useState<ISummary>(null);

  const context = useMemo(
    () => ({
      fbtProductsWithPrice,
      setFbtProductsWithPrice,
      isFbtSubTotalLoading,
      setIsFbtSubTotalLoading,
      subTotal,
      setSubTotal,
      currency,
      setCurrency,
      cartDetails,
      setCartDetails,
      itemSummary,
      setItemSummary
    }),
    [
      fbtProductsWithPrice,
      setFbtProductsWithPrice,
      isFbtSubTotalLoading,
      setIsFbtSubTotalLoading,
      subTotal,
      setSubTotal,
      currency,
      setCurrency,
      cartDetails,
      setCartDetails,
      itemSummary,
      setItemSummary
    ]
  );

  return <FbtContext.Provider value={context}>{children}</FbtContext.Provider>;
}

export function useFbtContext() {
  const context = useContext(FbtContext);
  if (!context) {
    throw new Error('useFbtContext must be used within a FbtContextProvider');
  }
  return context;
}
